import type { ComponentProps, ReactNode } from 'react';
import { FormProvider, useFormContext, useFormState } from 'react-hook-form';
import type { z } from 'zod';
import { Alert, AlertContent, AlertIndicator, AlertTitle } from '../tailgrids/core/alert.js';
import { Button, type ButtonProps } from '../tailgrids/core/button.js';
import { Spinner } from '../tailgrids/core/spinner/index.js';
import { NavigateAwayWarning } from '../entity/NavigateAwayWarning.js';
import { useDirtyGuard } from '../../hooks/useDirtyGuard.js';
import { cn } from '../../utils/cn.js';
import { applyServerErrors, ROOT_SERVER_ERROR } from './server-errors.js';
import type { FormSchema, ZodFormReturn } from './use-zod-form.js';

export interface FormProps<TSchema extends FormSchema> extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  form: ZodFormReturn<TSchema>;
  /**
   * Receives the schema-validated values. Throw (or reject) to fail the submit: an API error is
   * placed on the fields it names, anything else becomes the form-level error.
   */
  onSubmit: (values: z.output<TSchema>) => unknown;
  /** Warn before navigating away while there are unsaved changes. */
  guardUnsaved?: boolean;
}

/**
 * The one form element. Browser validation is off (`noValidate`): the schema validates, react-aria
 * renders each field's error, and a failed request's errors land on the fields they name.
 */
export function Form<TSchema extends FormSchema>({
  form,
  onSubmit,
  guardUnsaved = false,
  className,
  children,
  ...props
}: FormProps<TSchema>) {
  const { isDirty, isSubmitSuccessful } = form.formState;
  const guard = useDirtyGuard(guardUnsaved && isDirty && !isSubmitSuccessful);

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors(ROOT_SERVER_ERROR);
    try {
      await onSubmit(values);
    } catch (error) {
      applyServerErrors(form, error);
    }
  });

  return (
    <FormProvider {...form}>
      <form noValidate onSubmit={submit} className={cn('flex flex-col gap-5', className)} {...props}>
        <FormError />
        {children}
      </form>
      {guardUnsaved && (
        <NavigateAwayWarning open={guard.pendingNavigation} onLeave={guard.confirmLeave} onStay={guard.cancelLeave} />
      )}
    </FormProvider>
  );
}

/** The form-level error: a failed request that names no visible field. Rendered by <Form>. */
function FormError() {
  const { errors } = useFormState();
  const message = errors.root?.server?.message;
  if (!message) return null;
  return (
    <Alert status="error">
      <AlertIndicator />
      <AlertContent>
        <AlertTitle>{message}</AlertTitle>
      </AlertContent>
    </Alert>
  );
}

export interface SubmitButtonProps extends Omit<ButtonProps, 'type'> {
  children: ReactNode;
}

/** The form's primary action: disabled with a spinner while the submit is in flight. */
export function SubmitButton({ children, isDisabled, ...props }: SubmitButtonProps) {
  const { formState } = useFormContext();
  return (
    <Button type="submit" isDisabled={isDisabled || formState.isSubmitting} {...props}>
      {formState.isSubmitting && <Spinner size="sm" />}
      {children}
    </Button>
  );
}
